import { useState } from 'react'
import { Lock, NotebookText, Pencil, Plus, Trash2 } from 'lucide-react'
import { write } from '../api'
import { dateTime } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  Refusal,
} from '../ui'

// ======================================================================
// WORKING NOTES
// ======================================================================
//
// THE WORD "PRIVATE" DOES NOT APPEAR IN THIS FILE AND MUST NOT.
//
// er_cases/working_notes.py is explicit: these are working papers, they
// are not the case record, and they REMAIN DISCLOSABLE if the matter
// goes further. Calling them private would be a promise this system
// cannot keep, made to the person least able to check it — an
// investigator writing down a half-formed view about a named colleague,
// believing nobody else will ever read it.
//
// So the warning below is prominent, permanent and worded as a fact
// rather than a caveat. It is the first thing in the panel, not a
// footnote under the compose box.
//
// UNCOMMITTED notes are editable by their AUTHOR ONLY — not by a case
// owner, because editing somebody's rough thinking and leaving their
// name on it is the worst thing this table could permit. COMMITTED notes
// are frozen: the text is in er_evidence now, and nothing there is ever
// edited or deleted.

export default function WorkingNotes({ caseId, items, me, onReplace }) {
  const [dialog, setDialog] = useState(null)
  const [error, setError] = useState(null)

  const act = async (fn) => {
    setError(null)
    try {
      onReplace(await fn())
    } catch (e) {
      setError(e)
    }
  }

  const visible = items.filter((n) => !n.deleted_at)
  const withdrawn = items.filter((n) => n.deleted_at)

  return (
    <Panel
      eyebrow="Your working papers"
      title="Working notes"
      right={
        <Button size="sm" icon={Plus} onClick={() => setDialog({ kind: 'add' })}>
          Write a note
        </Button>
      }
    >
      <div className="mb-4">
        <Notice tone="amber" title="These are working papers, not the case record">
          <p>
            They are not private. If this matter goes to a tribunal or a subject
            access request, working notes can be disclosed along with everything
            else. Write them on the assumption that the person the case is about
            will read them.
          </p>
          <p className="mt-1.5">
            Committing a note moves it into the evidence record deliberately and
            permanently. Until then it can be edited or withdrawn by whoever
            wrote it, and by nobody else.
          </p>
        </Notice>
      </div>

      <Refusal error={error} onDismiss={() => setError(null)} />

      {visible.length === 0 && withdrawn.length === 0 ? (
        <Empty icon={NotebookText} title="You have not written any notes on this case" />
      ) : (
        <ul className="space-y-2.5">
          {visible.map((n) => {
            const frozen = !!n.committed_evidence_id
            const mine = me && n.authored_by === me
            return (
              <li key={n.note_id} className="panel-inset px-4 py-3.5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {frozen ? (
                        <Chip tone="blue">
                          committed to the record — frozen
                        </Chip>
                      ) : (
                        <Chip>not committed</Chip>
                      )}
                      {mine && <Chip tone="accent">yours</Chip>}
                    </div>
                    <p className="measure mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed">
                      {n.body}
                    </p>
                    <div className="mt-2 text-[11.5px] text-faint">
                      Written {dateTime(n.created_at)}
                      {n.updated_at !== n.created_at && (
                        <> · last changed {dateTime(n.updated_at)}</>
                      )}
                    </div>
                  </div>

                  {/* The controls appear only for a note this caller
                      wrote and has not committed. The server enforces
                      both; this only avoids offering a button that
                      exists to be refused. */}
                  {mine && !frozen && (
                    <div className="flex shrink-0 flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Pencil}
                        onClick={() => setDialog({ kind: 'edit', note: n })}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Lock}
                        onClick={() => setDialog({ kind: 'commit', note: n })}
                      >
                        Commit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Trash2}
                        onClick={() =>
                          act(() =>
                            write(`/${caseId}/notes/${n.note_id}`, { method: 'DELETE' }),
                          )
                        }
                      >
                        Withdraw
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            )
          })}

          {/* WITHDRAWN NOTES REACH THEIR AUTHOR ONLY — the SQL never
              returns another person's — and are shown flagged, so the
              author can see what they took back. The row is not
              deleted; nothing here is. */}
          {withdrawn.map((n) => (
            <li key={n.note_id} className="panel-inset px-4 py-3.5 opacity-60">
              <Chip tone="amber">withdrawn {dateTime(n.deleted_at)}</Chip>
              <p className="measure mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed line-through decoration-1">
                {n.body}
              </p>
            </li>
          ))}
        </ul>
      )}

      {dialog?.kind === 'add' && (
        <NoteDialog
          title="Write a working note"
          initial=""
          onClose={() => setDialog(null)}
          onSave={(body) => write(`/${caseId}/notes`, { body: { body } })}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'edit' && (
        <NoteDialog
          title="Edit your working note"
          initial={dialog.note.body}
          onClose={() => setDialog(null)}
          onSave={(body) =>
            write(`/${caseId}/notes/${dialog.note.note_id}`, {
              method: 'PATCH',
              body: { body },
            })
          }
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'commit' && (
        <CommitDialog
          caseId={caseId}
          note={dialog.note}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
    </Panel>
  )
}

function NoteDialog({ title, initial, onClose, onSave, onReplace }) {
  const [body, setBody] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle="Working papers. Not part of the case record until committed, and disclosable either way."
      width="max-w-2xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <Field label="Note" required>
        <textarea
          className="field"
          rows={10}
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!body.trim()}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onReplace(await onSave(body))
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Save
        </Button>
      </div>
    </Modal>
  )
}

function CommitDialog({ caseId, note, onClose, onReplace }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  return (
    <Modal
      open
      onClose={onClose}
      title="Commit this note to the record"
      subtitle="One way. Once committed it cannot be edited or withdrawn by anybody, including you."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <div className="mb-4">
        <Notice tone="amber" title="This cannot be undone">
          The text is written into the evidence record as an exhibit and hashed.
          Evidence has no delete and no edit, so un-committing would leave a hole
          in the exhibit list. Commit when you have decided this belongs in the
          case, not before.
        </Notice>
      </div>
      <p className="measure whitespace-pre-wrap rounded-md border border-line bg-surface-2 px-3.5 py-3 text-[12.5px] leading-relaxed">
        {note.body}
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onReplace(
                await write(`/${caseId}/notes/${note.note_id}/commit`, { body: {} }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Commit to the record
        </Button>
      </div>
    </Modal>
  )
}
